/* global gdAlertify */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosConfig";
import { formatMoney } from "../../utils/utils";
import { FIRMA_DEFAULT } from "../../utils/firmaCorreo";
import {
    REGLA_TIPOS_COBRANZA, TIPOS_CORREO_COBRANZA, renderizarVariables, textoInicialDeTipo, variablesDesconocidas, variablesEnTexto,
} from "../../utils/variablesCorreoCobranza";
import { BadgeEstado, mensajeError } from "./enviosUi";
import { MESES, imagenComoUrl } from "./honorariosWsp";

const MAX_PRUEBA = 10;
const tipoDef = (clave) => TIPOS_CORREO_COBRANZA.find((t) => t.clave === clave) || { label: clave, color: "light" };

export const BadgeTipo = ({ tipo }) => {
    const def = tipoDef(tipo);
    return <span className={`badge bg-${def.color} ${def.color === "warning" || def.color === "light" ? "text-dark" : ""}`}>{def.label}</span>;
};

/**
 * Envío masivo de los correos de cobranza de un período liquidado. Cada paciente recibe el
 * template activo de su TIPO (se crean en Templates de Correo; si no hay, el texto por defecto
 * del tipo), con sus variables, la firma y el PDF liviano del detalle (se genera al enviar).
 * Se revisa en la tabla (ver correo / PDF, prueba a un correo propio) y nada sale hasta
 * "Procesar cola ahora". En modo demo, 1 mensaje (al correo demo).
 */
const NotificarCobranzaModal = ({ visible, onClose, periodo }) => {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [errorCarga, setErrorCarga] = useState(null);
    const [tipoManual, setTipoManual] = useState({}); // paciente_id => tipo elegido a mano
    const [seleccion, setSeleccion] = useState(new Set());
    const [demoId, setDemoId] = useState(null);
    const [fase, setFase] = useState("listo"); // listo | creando | fin
    const [resultados, setResultados] = useState([]);
    const [loteId, setLoteId] = useState(null);
    const [vistaCorreo, setVistaCorreo] = useState(null); // candidato
    const [vistaPdf, setVistaPdf] = useState(null); // { nombre, url }
    const [destinoPrueba, setDestinoPrueba] = useState("");
    const [probando, setProbando] = useState(false);
    const [resultadoPrueba, setResultadoPrueba] = useState(null);

    const [anio, mes] = (periodo || "").split("-").map(Number);
    const periodoTexto = `${MESES[mes - 1] || ""} ${anio || ""}`;

    useEffect(() => {
        if (!visible || !periodo) return;
        setFase("listo");
        setResultados([]);
        setResultadoPrueba(null);
        setErrorCarga(null);
        setTipoManual({});
        (async () => {
            setCargando(true);
            try {
                const { data } = await axiosInstance.get("/envios/cobranza/estado", { params: { periodo } });
                setInfo(data?.data);
                setDestinoPrueba((actual) => actual || data?.data?.demo_email || "");
            } catch (error) {
                setErrorCarga(mensajeError(error, "No se pudo preparar la notificación."));
            } finally {
                setCargando(false);
            }
        })();
    }, [visible, periodo]);

    useEffect(() => () => { if (vistaPdf?.url) URL.revokeObjectURL(vistaPdf.url); }, [vistaPdf]);

    // Texto de cada tipo: su template activo (Templates de Correo) o el texto por defecto del tipo.
    const textos = useMemo(() => {
        if (!info) return {};
        return Object.fromEntries(TIPOS_CORREO_COBRANZA.map((t) => {
            const texto = textoInicialDeTipo(info, t.clave);
            const template = (info.templates || []).find((x) => String(x.id) === texto.templateId);
            return [t.clave, { ...texto, nombre: template?.nombre || null, usadas: variablesEnTexto(`${texto.asunto} ${texto.cuerpo}`) }];
        }));
    }, [info]);

    // Tipo final (detectado o elegido a mano), motivo de exclusión del servidor y variables vacías de su texto.
    const candidatos = useMemo(() => (info?.candidatos || []).map((c) => {
        const tipo = tipoManual[c.paciente_id] || c.tipo;
        const vacias = (textos[tipo]?.usadas || []).filter((v) => c.variables[v] !== undefined && String(c.variables[v]).trim() === "");
        const motivo = c.motivo || (vacias.length ? `Sin dato: {${vacias.join("}, {")}}` : null);
        return { ...c, tipo, tipoDetectado: c.tipo, motivo };
    }), [info, textos, tipoManual]);

    const elegibles = useMemo(() => candidatos.filter((c) => !c.motivo), [candidatos]);
    const idsElegibles = elegibles.map((c) => c.paciente_id).join(",");

    // Por defecto se marcan todos los que se pueden notificar (los sin deuda ya vienen excluidos).
    useEffect(() => {
        setSeleccion(new Set(elegibles.map((c) => c.paciente_id)));
        setDemoId((actual) => (elegibles.some((c) => c.paciente_id === actual) ? actual : elegibles[0]?.paciente_id ?? null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsElegibles]);

    const aEnviar = info?.modo_demo
        ? elegibles.filter((c) => c.paciente_id === demoId).slice(0, 1)
        : elegibles.filter((c) => seleccion.has(c.paciente_id));
    const tiposEnUso = [...new Set(aEnviar.map((c) => c.tipo))];

    const problemaTexto = (tipo) => {
        const desconocidas = variablesDesconocidas(`${textos[tipo]?.asunto} ${textos[tipo]?.cuerpo}`);
        return desconocidas.length ? `El template de "${tipoDef(tipo).label}" usa variables que no existen: {${desconocidas.join("}, {")}}.` : null;
    };

    const bloqueo = !info ? null
        : !info.liquidada ? `La cobranza de ${periodoTexto} no está liquidada: liquídala antes de notificar.`
        : !info.canal.configurado ? "El canal de correo no está configurado (Motor de Envíos > Canales)."
        : tiposEnUso.map(problemaTexto).find(Boolean) || null;

    // Lo que se manda al backend: el texto de cada tipo usado (con la firma) y el tipo de cada paciente.
    const payloadTextos = (lista) => JSON.stringify(Object.fromEntries([...new Set(lista.map((c) => c.tipo))].map((t) => [t, {
        asunto: textos[t].asunto,
        cuerpo: `${textos[t].cuerpo}<br/><br/>${FIRMA_DEFAULT}`,
    }])));
    const payloadAsignacion = (lista) => JSON.stringify(lista.map((c) => ({ paciente_id: c.paciente_id, tipo: c.tipo })));

    const alternar = (id) => setSeleccion((prev) => {
        const nuevo = new Set(prev);
        nuevo.has(id) ? nuevo.delete(id) : nuevo.add(id);
        return nuevo;
    });

    const verPdf = async (c) => {
        setVistaPdf({ nombre: c.nombre, url: null });
        try {
            setVistaPdf({ nombre: c.nombre, url: await imagenComoUrl("/envios/cobranza/pdf", { periodo, paciente_id: c.paciente_id }) });
        } catch (error) {
            setVistaPdf(null);
            gdAlertify.notiError(error.message);
        }
    };

    // Prueba: los correos armados igual que el masivo, al momento y solo al correo indicado.
    const enviarPrueba = async (lista) => {
        if (!lista.length) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(destinoPrueba.trim())) {
            gdAlertify.notiError("Indica el correo donde quieres recibir la prueba.");
            return;
        }
        setProbando(true);
        setResultadoPrueba(null);
        try {
            const { data } = await axiosInstance.post("/envios/cobranza/prueba", {
                periodo,
                destino: destinoPrueba.trim(),
                textos: payloadTextos(lista),
                asignacion: payloadAsignacion(lista),
            });
            const r = data?.data || {};
            const porId = Object.fromEntries(lista.map((c) => [c.paciente_id, c]));
            setResultadoPrueba({ ...r, resultados: (r.resultados || []).map((x) => ({ ...x, c: porId[x.paciente_id] })) });
            const resumen = Object.values(r.resumen || {})[0] || {};
            if (resumen.error_canal) gdAlertify.notiWarning(resumen.error_canal);
            else gdAlertify.notiSuccess(`Prueba enviada a ${destinoPrueba.trim()}.`);
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo enviar la prueba."));
        } finally {
            setProbando(false);
        }
    };

    // Muestra: un paciente por tipo entre los que se van a notificar.
    const muestraPorTipo = TIPOS_CORREO_COBRANZA.map((t) => aEnviar.find((c) => c.tipo === t.clave)).filter(Boolean);

    const crearEnvio = async () => {
        if (bloqueo || !aEnviar.length) return;
        setFase("creando");
        try {
            const { data } = await axiosInstance.post("/envios/cobranza/lote", { periodo });
            const id = data?.data?.id;
            setLoteId(id);
            const resp = await axiosInstance.post(`/envios/cobranza/lote/${id}/mensajes`, {
                textos: payloadTextos(aEnviar),
                asignacion: payloadAsignacion(aEnviar),
            });
            const porId = Object.fromEntries(aEnviar.map((c) => [c.paciente_id, c]));
            setResultados((resp.data?.data || []).map((r) => ({ ...r, c: porId[r.paciente_id] })));
            setFase("fin");
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo crear el envío."));
            setFase("listo");
        }
    };

    if (!visible) return null;

    const textoCrear = info?.modo_demo
        ? "Crear envío de prueba (1 correo)"
        : `Notificar ${aEnviar.length === elegibles.length ? "a todos" : "seleccionados"} (${aEnviar.length})`;
    const botonCrear = (
        <button className="btn btn-success" onClick={crearEnvio} disabled={!!bloqueo || !aEnviar.length || cargando || fase === "creando"}>
            {fase === "creando" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-envelope me-1"></i>}
            {textoCrear}
        </button>
    );

    const creados = resultados.filter((r) => r.ok);
    const omitidos = resultados.filter((r) => r.omitido);
    const conError = resultados.filter((r) => !r.ok && !r.omitido);
    const editable = fase === "listo";
    const sinTemplate = TIPOS_CORREO_COBRANZA.filter((t) => aEnviar.some((c) => c.tipo === t.clave) && !textos[t.clave]?.templateId);

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header bg-dark text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-envelope-paper me-2"></i>
                            Notificar cobranza por correo · {periodoTexto}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={fase === "creando"}></button>
                    </div>

                    <div className="modal-body">
                        {cargando && <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>}
                        {errorCarga && <div className="alert alert-danger">{errorCarga}</div>}

                        {!cargando && info && (
                            <>
                                {bloqueo && <div className="alert alert-danger"><i className="bi bi-x-octagon me-2"></i>{bloqueo}</div>}

                                {info.modo_demo ? (
                                    <div className="alert alert-warning py-2">
                                        <i className="bi bi-cone-striped me-2"></i>
                                        <strong>Modo DEMO:</strong> se crea <strong>1 correo</strong> (el del paciente elegido en la tabla) y sale a{" "}
                                        <strong>{info.demo_email || "(sin configurar)"}</strong>, sin copias y sin marcar la notificación.
                                    </div>
                                ) : (
                                    <div className="alert alert-success py-2">
                                        <i className="bi bi-broadcast me-2"></i>
                                        <strong>Modo PRODUCTIVO:</strong> un correo por paciente seleccionado, con el template de su tipo.
                                        Al enviarse sube su número de notificación.
                                    </div>
                                )}

                                <div className="small text-muted mb-2">
                                    <i className="bi bi-info-circle me-1"></i>{REGLA_TIPOS_COBRANZA} Los templates se editan en <strong>Templates de Correo</strong>.
                                    {sinTemplate.length > 0 && (
                                        <span className="text-danger"> Sin template activo (usa el texto por defecto): {sinTemplate.map((t) => t.label).join(", ")}.</span>
                                    )}
                                </div>

                                {fase !== "fin" && (
                                    <div className="d-flex flex-wrap align-items-center gap-2 p-2 mb-2 border rounded bg-light">
                                        <span className="small fw-semibold"><i className="bi bi-eyeglasses me-1"></i>Probar antes de enviar:</span>
                                        <input className="form-control form-control-sm" style={{ maxWidth: 260 }} placeholder="tu correo"
                                            value={destinoPrueba} onChange={(e) => setDestinoPrueba(e.target.value)} />
                                        <button className="btn btn-sm btn-outline-primary" disabled={probando || !muestraPorTipo.length || !!bloqueo}
                                            onClick={() => enviarPrueba(muestraPorTipo)}
                                            title="Te llega un correo de cada tipo (el primer paciente seleccionado de cada uno)">
                                            {probando ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-send me-1"></i>}
                                            1 por tipo ({muestraPorTipo.length})
                                        </button>
                                        <button className="btn btn-sm btn-outline-secondary" disabled={probando || !aEnviar.length || !!bloqueo}
                                            onClick={() => enviarPrueba(aEnviar.slice(0, MAX_PRUEBA))}
                                            title={`Los primeros ${MAX_PRUEBA} seleccionados`}>
                                            Seleccionados (máx. {MAX_PRUEBA})
                                        </button>
                                        <span className="small text-muted">Llegan con [PRUEBA] en el asunto, armados igual que el envío real. No marcan nada.</span>
                                    </div>
                                )}

                                {resultadoPrueba && (
                                    <div className="alert alert-light border py-2 small">
                                        <i className="bi bi-envelope-check me-1"></i>
                                        Prueba #{resultadoPrueba.lote_id || "—"} a {destinoPrueba}:{" "}
                                        {resultadoPrueba.resultados.map((r) => (
                                            <span key={r.paciente_id} className={`me-2 ${r.ok ? "" : "text-danger"}`}>
                                                {r.c?.nombre || r.paciente_id} ({tipoDef(r.tipo).label}){r.ok ? "" : `: ${r.error}`}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {fase === "listo" && (
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                        <small className="text-muted">
                                            <strong>{aEnviar.length}</strong> por notificar
                                            {" "}({TIPOS_CORREO_COBRANZA.map((t) => [t, aEnviar.filter((c) => c.tipo === t.clave).length]).filter(([, n]) => n).map(([t, n]) => `${n} ${t.label.toLowerCase()}`).join(" · ") || "ninguno"})
                                            {" "}· {candidatos.length - elegibles.length} excluido(s) · {candidatos.length} en total
                                        </small>
                                        {botonCrear}
                                    </div>
                                )}

                                {fase !== "fin" && (
                                    <div className="border rounded" style={{ maxHeight: "60vh", overflow: "auto" }}>
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: 32 }}>
                                                        {!info.modo_demo && (
                                                            <input type="checkbox" className="form-check-input" disabled={!editable}
                                                                checked={elegibles.length > 0 && seleccion.size === elegibles.length}
                                                                onChange={(e) => setSeleccion(new Set(e.target.checked ? elegibles.map((c) => c.paciente_id) : []))} />
                                                        )}
                                                    </th>
                                                    <th>Paciente</th>
                                                    <th>Tipo</th>
                                                    <th>Template</th>
                                                    <th>Correo</th>
                                                    <th className="text-center">Notif.</th>
                                                    <th className="text-end">Total deuda</th>
                                                    <th>Estado</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {candidatos.map((c) => (
                                                    <tr key={c.paciente_id} className={c.motivo ? "text-muted" : ""}>
                                                        <td>
                                                            {info.modo_demo ? (
                                                                <input type="radio" className="form-check-input" name="demo" disabled={!!c.motivo || !editable}
                                                                    checked={demoId === c.paciente_id} onChange={() => setDemoId(c.paciente_id)} />
                                                            ) : (
                                                                <input type="checkbox" className="form-check-input" disabled={!!c.motivo || !editable}
                                                                    checked={!c.motivo && seleccion.has(c.paciente_id)} onChange={() => alternar(c.paciente_id)} />
                                                            )}
                                                        </td>
                                                        <td>{c.nombre}</td>
                                                        <td>
                                                            <select className={`form-select form-select-sm py-0 ${c.tipo !== c.tipoDetectado ? "border-primary" : ""}`}
                                                                style={{ minWidth: 150 }} value={c.tipo} disabled={!editable}
                                                                title={c.tipo !== c.tipoDetectado ? `Detectado: ${tipoDef(c.tipoDetectado).label}` : "Detectado automáticamente"}
                                                                onChange={(e) => setTipoManual((prev) => ({ ...prev, [c.paciente_id]: e.target.value }))}>
                                                                {TIPOS_CORREO_COBRANZA.map((t) => <option key={t.clave} value={t.clave}>{t.label}</option>)}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <small className={textos[c.tipo]?.templateId ? "" : "text-danger"}>
                                                                {textos[c.tipo]?.nombre || "Texto por defecto"}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <small>{c.correos[0] || "—"}</small>
                                                            {c.correos.length > 1 && <small className="text-muted d-block">cc: {c.correos.slice(1).join(", ")}</small>}
                                                            {c.correos.length > 0 && c.correos_invalidos.length > 0 && (
                                                                <small className="text-danger d-block">Se omite (inválido): {c.correos_invalidos.join(", ")}</small>
                                                            )}
                                                        </td>
                                                        <td className="text-center"><small>{c.numero_notificacion} → <strong>{c.numero_notificacion_siguiente}</strong></small></td>
                                                        <td className="text-end">${formatMoney(c.montos.total_deuda)}</td>
                                                        <td>
                                                            {c.motivo === "Ya está en la cola" ? <BadgeEstado estado={c.previo.estado} />
                                                                : c.motivo ? <span className="badge bg-secondary text-wrap text-start">{c.motivo}</span>
                                                                : c.previo?.estado === "fallido" ? <span className="badge bg-warning text-dark">Falló antes</span>
                                                                : <span className="badge bg-light text-dark border">Por notificar</span>}
                                                        </td>
                                                        <td className="text-end text-nowrap">
                                                            <button className="btn btn-sm btn-link p-0 me-2" title="Ver el correo que se enviará" onClick={() => setVistaCorreo(c)}>
                                                                <i className="bi bi-eye"></i>
                                                            </button>
                                                            <button className="btn btn-sm btn-link p-0 me-2" title="Ver el PDF del detalle" onClick={() => verPdf(c)}>
                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                            </button>
                                                            <button className="btn btn-sm btn-link p-0" title="Enviarme la prueba de este paciente"
                                                                disabled={probando || !!problemaTexto(c.tipo)} onClick={() => enviarPrueba([c])}>
                                                                <i className="bi bi-send"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {!candidatos.length && (
                                                    <tr><td colSpan="9" className="text-center text-muted py-3">No hay pacientes en la cobranza de este período.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {fase === "fin" && (
                                    <div className="alert alert-light border">
                                        <div>
                                            <i className="bi bi-check-circle text-success me-1"></i>
                                            <strong>{creados.length}</strong> correo(s) en la cola del envío <strong>#{loteId}</strong> (pendientes):{" "}
                                            {TIPOS_CORREO_COBRANZA.filter((t) => creados.some((r) => r.tipo === t.clave))
                                                .map((t) => `${creados.filter((r) => r.tipo === t.clave).length} ${t.label.toLowerCase()}`).join(" · ")}
                                        </div>
                                        {omitidos.length > 0 && <div><i className="bi bi-skip-forward me-1"></i>{omitidos.length} omitido(s): {omitidos.map((r) => `${r.c?.nombre || r.paciente_id} (${r.motivo})`).join("; ")}.</div>}
                                        {conError.length > 0 && (
                                            <div className="mt-2">
                                                <i className="bi bi-x-circle text-danger me-1"></i>{conError.length} con error:
                                                <ul className="mb-0 small">
                                                    {conError.map((r) => <li key={r.paciente_id}>{r.c?.nombre || r.paciente_id}: {r.error}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="mt-2 small">
                                            Todavía no sale nada: revísalos en <strong>Motor de Envíos</strong> (cada mensaje muestra su correo y PDF) y
                                            envíalos con <strong>Procesar cola ahora</strong>. Si algo no está bien, cancela el envío.
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={fase === "creando"}>Cerrar</button>
                        {fase === "fin" && (
                            <button className="btn btn-primary" onClick={() => navigate("/motor-envios")}>
                                <i className="bi bi-send me-1"></i>Ir al Motor de Envíos
                            </button>
                        )}
                        {fase !== "fin" && botonCrear}
                    </div>
                </div>
            </div>

            {vistaCorreo && (() => {
                const texto = textos[vistaCorreo.tipo] || { asunto: "", cuerpo: "" };
                return (
                    <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", overflowY: "auto", zIndex: 1060 }}
                        onClick={() => setVistaCorreo(null)}>
                        <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <div>
                                        <h6 className="modal-title mb-0">{renderizarVariables(texto.asunto, vistaCorreo.variables, false)}</h6>
                                        <small className="text-muted">
                                            <BadgeTipo tipo={vistaCorreo.tipo} /> {texto.nombre || "Texto por defecto"} · Para: {vistaCorreo.correos[0] || "—"}
                                            {vistaCorreo.correos.length > 1 && ` · cc: ${vistaCorreo.correos.slice(1).join(", ")}`}
                                        </small>
                                    </div>
                                    <button type="button" className="btn-close" onClick={() => setVistaCorreo(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <div dangerouslySetInnerHTML={{ __html: renderizarVariables(texto.cuerpo, vistaCorreo.variables) }} />
                                    <hr />
                                    <div dangerouslySetInnerHTML={{ __html: FIRMA_DEFAULT }} />
                                    <div className="mt-3 small text-muted"><i className="bi bi-paperclip me-1"></i>PDF del detalle de cobranza adjunto.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {vistaPdf && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", overflowY: "auto", zIndex: 1060 }}
                    onClick={() => setVistaPdf(null)}>
                    <div className="modal-dialog modal-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h6 className="modal-title">PDF del detalle · {vistaPdf.nombre}</h6>
                                <button type="button" className="btn-close" onClick={() => setVistaPdf(null)}></button>
                            </div>
                            <div className="modal-body p-0">
                                {vistaPdf.url
                                    ? <iframe title="PDF del detalle" src={vistaPdf.url} style={{ width: "100%", height: "75vh", border: 0 }} />
                                    : <div className="py-5 text-center"><span className="spinner-border text-primary"></span></div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificarCobranzaModal;
